import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  deleteVendorsSocialLinks,
  setVendorsSocialLinks,
  subscribeManyVendorsSocialLinks,
  updateVendorsSocialLinks,
  VendorsSocialLinksResponse,
} from '@/lib/collections/vendors';
import { generateRandomId } from '@/lib/utils';
import { Link2, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

interface SocialLinksEditorProps {
  vendorId: string;
  disabled?: boolean;
}

/**
 * Dynamic add/remove rows of { label, url } backed by the vendors/{id}/socialLinks
 * sub-collection. Add creates a new doc immediately; remove deletes it. Each
 * row's fields persist on blur.
 */
export const SocialLinksEditor: React.FC<SocialLinksEditorProps> = ({ vendorId, disabled }) => {
  const { data } = useRealtimeData<VendorsSocialLinksResponse[]>(
    subscribeManyVendorsSocialLinks,
    !!vendorId,
    vendorId
  );

  const links = data ?? [];
  const [addingRow, setAddingRow] = useState(false);

  const handleAddRow = async () => {
    setAddingRow(true);
    try {
      const linkId = generateRandomId();
      const success = await setVendorsSocialLinks(vendorId, linkId, { label: '', url: '' });
      if (!success) toast.error('Could not add link.');
    } catch (error) {
      console.error('Failed to add social link', error);
      toast.error('Something went wrong.');
    } finally {
      setAddingRow(false);
    }
  };

  const handleRemoveRow = async (linkId: string) => {
    try {
      const success = await deleteVendorsSocialLinks(vendorId, linkId);
      if (!success) toast.error('Could not remove link.');
    } catch (error) {
      console.error('Failed to remove social link', error);
      toast.error('Something went wrong.');
    }
  };

  return (
    <div>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-semibold text-foreground'>Social links</span>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={disabled || addingRow}
          onClick={handleAddRow}
          className='gap-1.5 rounded-full border-border text-xs'
        >
          <Plus className='h-3.5 w-3.5' />
          Add link
        </Button>
      </div>

      {links.length === 0 ? (
        <p className='mt-2 text-xs text-muted-foreground'>
          No social links yet — add Instagram, WhatsApp, or a storefront link.
        </p>
      ) : (
        <div className='mt-3 space-y-2.5'>
          {links.map((link) => (
            <SocialLinkRow
              key={link.id}
              vendorId={vendorId}
              link={link}
              disabled={disabled}
              onRemove={() => handleRemoveRow(link.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface SocialLinkRowProps {
  vendorId: string;
  link: VendorsSocialLinksResponse;
  disabled?: boolean;
  onRemove: () => void;
}

const SocialLinkRow: React.FC<SocialLinkRowProps> = ({ vendorId, link, disabled, onRemove }) => {
  const [label, setLabel] = useState(link.label);
  const [url, setUrl] = useState(link.url);

  const handleBlur = async (field: 'label' | 'url') => {
    const value = field === 'label' ? label : url;
    if (value === link[field]) return;
    try {
      const success = await updateVendorsSocialLinks(vendorId, link.id, { [field]: value });
      if (!success) toast.error('Could not save link.');
    } catch (error) {
      console.error('Failed to update social link', error);
      toast.error('Something went wrong.');
    }
  };

  return (
    <div className='flex items-center gap-2'>
      <Link2 className='h-4 w-4 shrink-0 text-muted-foreground' />
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => handleBlur('label')}
        placeholder='Label (e.g. Instagram)'
        disabled={disabled}
        className='w-36 shrink-0 rounded-full'
      />
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onBlur={() => handleBlur('url')}
        placeholder='https://...'
        disabled={disabled}
        className='flex-1 rounded-full'
      />
      <Button
        type='button'
        variant='ghost'
        size='icon'
        disabled={disabled}
        onClick={onRemove}
        className='shrink-0 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
        aria-label='Remove link'
      >
        <Trash2 className='h-4 w-4' />
      </Button>
    </div>
  );
};

export default SocialLinksEditor;
